import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import { Button, ConfirmDialog, DataTable, Field, LoadMore, Notice, ProblemNotice, type Column } from '../../components/ui'
import { usePendingCount } from '../../contexts/PendingCountContext'
import { type AdminUserView, approveUser, listPendingUsers, rejectUser } from '../../lib/api'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { formatDateTime, relativeTime } from '../../lib/format'

/**
 * The approval queue (§5.2) — "the screen AIVY has no equivalent for".
 *
 * Oldest first, which is the backend's ordering (`application/admin.py:183`)
 * and also the right one: the oldest request has been waiting longest. The wait
 * is shown relatively as well as absolutely, because "3 days ago" is the fact
 * an operator is actually weighing.
 */
export function ApprovalQueue() {
  const { setCount } = usePendingCount()
  const [rows, setRows] = useState<AdminUserView[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [isLoading, setLoading] = useState(true)
  const [isPaging, setPaging] = useState(false)
  // See `Users.tsx` for the shape and the reason.
  /**
   * A retry is named, NOT captured.
   *
   * It was `retry?: () => void`, built at the moment of failure — so the page
   * retry held the cursor from the render that failed. Measured: two clicks
   * after a failed `loadMore` issued four requests with cursors
   * [null, CUR, CUR, CUR], walking straight past `if (!cursor) return` because
   * the closure's `cursor` was stale, and re-appending a page that was already
   * on screen. Naming the retry and resolving it at RENDER time means it always
   * runs against current state.
   */
  const [error, setError] = useState<{ text: string; cause: unknown; retry?: 'list' | 'page' } | null>(null)
  // The notice above unmounts when this clears; see `useRetryLanding`.
  const [landOn, armLanding] = useRetryLanding(error !== null)
  const [reloadToken, setReloadToken] = useState(0)
  const retryList = useCallback(() => {
    setLoading(true)
    setReloadToken((n) => n + 1)
  }, [])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<AdminUserView | null>(null)
  const [reason, setReason] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  /**
   * The first page.
   *
   * The effect body starts an async read and returns a cancel — it sets no
   * state synchronously, which is what `react-hooks/set-state-in-effect` asks
   * for and is genuinely better: `isLoading` already starts `true`, so the
   * "turn loading on" render never happens, and a unmount mid-flight cannot
   * write to a gone component.
   */
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const page = await listPendingUsers({})
        if (cancelled) return
        setRows(page.items)
        setCursor(page.next_cursor ?? null)
        // The badge counts what is KNOWN. A partial page would otherwise read
        // as the whole queue, so an unfinished list reports nothing at all.
        setCount(page.next_cursor ? null : page.items.length)
        // CLEARED ON SUCCESS, which this effect never did. `Users.tsx` and
        // `Audit.tsx` both do, and the asymmetry was invisible until Stage 7b
        // gave this screen a retry: before that the notice appeared once and
        // the operator reloaded the browser. With a retry, a SUCCESSFUL one
        // left the failure sentence, its Reference and its own "Try again"
        // sitting above the rows it had just loaded -- indistinguishable from
        // a retry that failed, on the route an administrator lands on.
        setError(null)
      } catch (err) {
        if (!cancelled) setError({ text: 'The queue could not be loaded.', cause: err, retry: 'list' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [setCount, reloadToken, retryList])

  const loadMore = async () => {
    if (!cursor) return
    setPaging(true)
    setError(null)
    try {
      const page = await listPendingUsers({ cursor })
      setRows((prev) => {
        const next = [...prev, ...page.items]
        setCount(page.next_cursor ? null : next.length)
        return next
      })
      setCursor(page.next_cursor ?? null)
      setError(null)
    } catch (err) {
      setError({ text: 'The next page could not be loaded.', cause: err, retry: 'page' })
    } finally {
      setPaging(false)
    }
  }

  /**
   * Where focus goes when the row it was on stops existing.
   *
   * `settle` removes the row it just acted on, so the Approve or Reject button
   * holding focus is gone in the same commit. `Button`'s own restore cannot
   * help — there is nothing left to restore to — and measured before this,
   * focus fell to `<body>`: an operator working the queue by keyboard was
   * returned to the top of the document after every decision.
   *
   * The notice is the honest target. It is what now says what happened, so
   * landing on it answers the question the keypress asked. Only when focus
   * actually fell: `document.activeElement` is never null (trap 42), so `body`
   * is the signal, and an operator who moved on keeps their place.
   *
   * A layout effect rather than a call inside `settle`, because the notice does
   * not exist until the commit that renders it.
   */
  const noticeRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (!notice) return
    if (document.activeElement === document.body) noticeRef.current?.focus()
  }, [notice])

  const settle = async (user: AdminUserView, run: () => Promise<unknown>, done: string) => {
    setBusyId(user.user_id)
    setError(null)
    try {
      await run()
      // Removed locally rather than refetched: the row has left the queue by
      // definition, and a refetch would discard pages the operator has loaded.
      setRows((prev) => {
        const remaining = prev.filter((r) => r.user_id !== user.user_id)
        if (!cursor) setCount(remaining.length)
        return remaining
      })
      setNotice(`${user.email ?? user.user_id} — ${done}.`)
    } catch (err) {
      setError({ text: 'That did not go through.', cause: err })
    } finally {
      setBusyId(null)
    }
  }

  const columns: Column<AdminUserView>[] = [
    { key: 'email', header: 'Email', render: (u) => <span className="mono-cell">{u.email ?? u.user_id}</span> },
    {
      key: 'waiting',
      header: 'Waiting',
      render: (u) => <span title={formatDateTime(u.created_at)}>{relativeTime(u.created_at)}</span>,
    },
    { key: 'verified', header: 'Email verified', render: (u) => (u.email_verified ? 'Yes' : 'Not yet') },
  ]

  return (
    <div className="admin-page stack stack--wide">
      <header className="page">
        <h1>Approval queue</h1>
        <p className="page__lead">
          Accounts waiting for a decision, oldest first. Until one is approved it can
          sign in and see nothing else.
        </p>
      </header>

      {error && (
        <ProblemNotice
          error={error.cause}
          onRetry={error.retry === 'list' ? retryList : error.retry === 'page' ? () => void loadMore() : undefined}
          retryBusy={error.retry === 'page' && isPaging}
          landing={armLanding}
        >
          {error.text}
        </ProblemNotice>
      )}
      {notice && <Notice tone="info" ref={noticeRef} tabIndex={-1}>{notice}</Notice>}

      {/* WHERE FOCUS GOES WHEN THE RETRY ABOVE WORKS.
          The notice unmounts when the error clears, taking the focused "Try
          again" with it -- `Button`'s own restore cannot help, because there is
          nothing left to restore to. The list is what now says what happened,
          so a keyboard user lands on the answer rather than at the top of the
          document. `tabIndex={-1}` keeps it out of the tab order; the global
          `:focus-visible` ring (base.css:62) is the only styling it needs. */}
      <div className="retry-landing" ref={landOn} tabIndex={-1}>
        <DataTable
          caption="Accounts awaiting approval"
          columns={columns}
          rows={rows}
          rowKey={(u) => u.user_id}
          isLoading={isLoading}
          empty={
            <>
              <strong>Nothing is waiting.</strong>
              <span>Every account that has signed up has had a decision.</span>
            </>
          }
          actions={(u) => (
            <div className="queue-actions">
              <Button
                size="sm"
                busy={busyId === u.user_id}
                onClick={() => void settle(u, () => approveUser(u.user_id), 'approved')}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busyId === u.user_id}
                onClick={() => { setRejecting(u); setReason('') }}
              >
                Reject
              </Button>
              <Link className="auth__link" to={`/admin/users/${encodeURIComponent(u.user_id)}`}>Details</Link>
            </div>
          )}
        />

        {!!rows.length && (
          <LoadMore
            loaded={rows.length}
            nextCursor={cursor}
            isLoading={isPaging}
            noun="account"
            onLoadMore={() => void loadMore()}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!rejecting}
        title="Reject this account?"
        consequence={
          <>
            {rejecting?.email ?? rejecting?.user_id} will not be able to use BrainSpot.
            They keep their sign-in, and see a screen saying the request was not approved.
          </>
        }
        detail={
          <>
            Your reason goes to the audit log and is <strong>not</strong> shown to them.
            An administrator can approve the account later — rejecting is reversible and
            erases nothing.
          </>
        }
        confirmLabel="Reject account"
        destructive
        busy={busyId === rejecting?.user_id}
        onCancel={() => setRejecting(null)}
        onConfirm={() => {
          const user = rejecting
          if (!user) return
          setRejecting(null)
          void settle(user, () => rejectUser(user.user_id, reason), 'rejected')
        }}
      >
        <Field
          label="Reason (optional)"
          value={reason}
          hint="Recorded in the audit log. Never shown to the person rejected."
          onChange={(e) => setReason(e.target.value)}
        />
      </ConfirmDialog>
    </div>
  )
}
